import { useCallback, useEffect, useState } from "react";

import {
  fetchEmployeeTechStacks,
  fetchTechStackMaster,
  deleteEmployeeTechStack,
  saveEmployeeTechStacks,
} from "../services/employee-tech-stack.service.ts";
import type {
  EmployeeTechStackResponse,
  TechStackCategory,
  UpdateEmployeeTechStackRequest,
} from "../types.ts";

export function useEmployeeTechStack(
  accessToken?: string,
  employeeId?: number,
) {
  const [categories, setCategories] = useState<TechStackCategory[]>([]);
  const [employeeTechStack, setEmployeeTechStack] =
    useState<EmployeeTechStackResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!accessToken || employeeId == null) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    async function load() {
      setLoading(true);
      setError("");
      try {
        const [master, employee] = await Promise.all([
          fetchTechStackMaster(accessToken, controller.signal),
          fetchEmployeeTechStacks(
            accessToken,
            controller.signal,
          ),
        ]);
        if (controller.signal.aborted) return;
        setCategories(master);
        setEmployeeTechStack(employee);
      } catch {
        if (!controller.signal.aborted) {
          setError("Unable to load technical stack.");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    void load();
    return () => controller.abort();
  }, [accessToken, employeeId]);

  const save = useCallback(
    async (request: UpdateEmployeeTechStackRequest) => {
      setSaving(true);
      try {
        await saveEmployeeTechStacks(request, accessToken);
      } finally {
        setSaving(false);
      }
    },
    [accessToken],
  );

  const remove = useCallback(
    async (techStackId: number) => {
      if (employeeId == null) return;
      await deleteEmployeeTechStack(employeeId, techStackId, accessToken);
    },
    [accessToken, employeeId],
  );

  return {
    categories,
    employeeTechStack,
    loading,
    saving,
    error,
    save,
    remove,
  };
}
